// Native-feeling pull-to-refresh, mobile-only. Mounts globally in
// Layout so every page gets it.
//
// Why we built this ourselves instead of using a library: we have a
// floating mini-player + a sticky chip-rail at the top, and most
// off-the-shelf PTR libs assume the page scrolls the full window
// from y=0. Ours threads the gesture through the actual
// `document.scrollingElement.scrollTop === 0` check, and re-uses
// the same touch-events plumbing we already have for swipe-down-
// to-minimize.
//
// Auth-safety:
//   • Triggers `window.location.reload()` on commit. Our auth token
//     is in localStorage (`hw:token`) so a hard reload re-hydrates
//     /auth/me with the existing token — the user stays signed in.
//   • Service-worker caches all the static bundles, so the reload
//     feels nearly instant on a warm cache.
//
// We intentionally do NOT add this to ChatRoom / LiveKit pages
// because reloading would drop their WebSocket connection mid-call.
// Those pages bail out early via the `excludePaths` allow-list.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowClockwise } from "@phosphor-icons/react";
import { useLocation } from "react-router-dom";

const TRIGGER_PX = 90;       // committed once dragged this far down
const MAX_DRAG = 140;        // rubber-band cap
const EXCLUDE_PATHS = [
  "/rooms/",                  // any specific chat room
  "/live/",                   // any livekit live stream
];

export default function PullToRefresh() {
  const start = useRef(null);
  const [dy, setDy] = useState(0);
  const [armed, setArmed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const location = useLocation();

  // Skip on live / room pages — reloading kills the WS connection.
  const enabled = !EXCLUDE_PATHS.some((p) => location.pathname.startsWith(p));

  const isAtTop = () => {
    // Use whichever element is actually the scroll root in this
    // browser. iOS Safari uses <body>, most others use
    // documentElement.
    const el = document.scrollingElement || document.documentElement;
    return (el?.scrollTop || 0) <= 0;
  };

  const onTouchStart = useCallback((e) => {
    if (!enabled || refreshing) return;
    if (!isAtTop()) return;
    const t = e.touches && e.touches[0];
    if (!t) return;
    start.current = { x: t.clientX, y: t.clientY };
    setArmed(false);
  }, [enabled, refreshing]);

  const onTouchMove = useCallback((e) => {
    if (!start.current || refreshing) return;
    const t = e.touches && e.touches[0];
    if (!t) return;
    const dx = t.clientX - start.current.x;
    const movedY = t.clientY - start.current.y;
    if (movedY <= 0 || Math.abs(dx) > Math.abs(movedY) * 0.6) {
      setDy(0);
      setArmed(false);
      return;
    }
    // Rubber-band the drag so it slows as it approaches MAX_DRAG.
    const damped = Math.min(MAX_DRAG, movedY * 0.5);
    setDy(damped);
    setArmed(damped >= TRIGGER_PX);
  }, [refreshing]);

  const onTouchEnd = useCallback(() => {
    if (!start.current) return;
    if (armed) {
      // Animate the indicator UP into a "loading" state, then reload.
      setRefreshing(true);
      setDy(70);
      // Tiny delay so the user sees the loader spin once before the
      // page actually nukes itself — feels intentional.
      setTimeout(() => {
        try { window.location.reload(); } catch (_) {}
      }, 400);
    } else {
      setDy(0);
    }
    start.current = null;
    setArmed(false);
  }, [armed]);

  useEffect(() => {
    if (!enabled) return;
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [enabled, onTouchStart, onTouchMove, onTouchEnd]);

  if (!enabled || dy <= 4) return null;
  const opacity = Math.min(1, dy / 40);
  const rotate = Math.min(360, dy * 4); // spins as you pull
  return (
    <div
      className="fixed top-0 left-0 right-0 z-[1000] flex justify-center pointer-events-none"
      style={{
        transform: `translateY(${Math.max(0, dy - 30)}px)`,
        transition: refreshing ? "transform 240ms cubic-bezier(.2,.8,.2,1)" : "none",
      }}
      data-testid="pull-to-refresh-indicator"
    >
      <div
        className={`mt-2 grid place-items-center w-10 h-10 rounded-full shadow-lg backdrop-blur-md transition-colors ${
          armed || refreshing ? "bg-terracotta text-cream" : "bg-cream text-forest"
        }`}
        style={{ opacity }}
      >
        <ArrowClockwise
          size={20}
          weight="bold"
          style={{
            transform: `rotate(${refreshing ? 0 : rotate}deg)`,
            animation: refreshing ? "spin 0.8s linear infinite" : "none",
            transition: refreshing ? "none" : "transform 60ms linear",
          }}
        />
      </div>
    </div>
  );
}
